import { Phone, Mail, MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import ChapterHeading from "@/components/ChapterHeading";
import Reveal from "@/components/Reveal";
import TradifyForm from "@/components/TradifyForm";
import usePageMeta from "@/hooks/usePageMeta";
import { useContent } from "@/content/ContentContext";
import { SITE } from "@/siteConfig";
import { SUBURBS_DETAILED } from "@/data/suburbs";

export default function Contact() {
  const { get } = useContent();
  usePageMeta(
    get("meta_contact_title", "Contact Modify | Grab Rails & Ramps Perth | Free Quotes"),
    get("meta_contact_desc", "Call Modify on 0480 554 777 or request a free quote online. Grab rails, hand rails and rubber ramps supplied and installed across Perth, WA.")
  );
  const phoneHref = get("site_phone_href", SITE.phoneHref);
  const phoneDisplay = get("site_phone_display", SITE.phoneDisplay);
  const email = get("site_email", SITE.email);
  return (
    <div data-testid="contact-page">
      <section className="bg-ivory">
        <div className="mx-auto max-w-7xl px-4 pb-20 pt-44 sm:px-6 lg:px-8 lg:pt-40">
          <ChapterHeading
            num="01"
            eyebrow="Contact"
            title="LET'S MAKE YOUR HOME SAFER."
            copy="Have a question about grab rails, hand rails or rubber ramps? Get in touch with Modify."
            testid="contact-heading"
          />

          <div className="mt-14 grid gap-10 lg:grid-cols-12">
            <Reveal className="lg:col-span-7">
              <div className="rounded-3xl border border-line bg-white p-3 shadow-lg sm:p-4">
                <TradifyForm testid="contact-tradify-form" />
              </div>
            </Reveal>

            <div className="space-y-5 lg:col-span-5">
              <Reveal delay={0.05}>
                <a
                  href={phoneHref}
                  data-testid="contact-phone-card"
                  className="group flex items-center gap-5 rounded-3xl bg-charcoal p-7 text-ivory transition-colors duration-300 hover:bg-ink"
                >
                  <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-white/10">
                    <Phone className="h-6 w-6" aria-hidden="true" />
                  </span>
                  <span>
                    <span className="block text-xs font-bold uppercase tracking-[0.2em] text-sage-tint">
                      Call Modify
                    </span>
                    <span className="mt-1 block font-display text-2xl font-bold">{phoneDisplay}</span>
                  </span>
                </a>
              </Reveal>

              <Reveal delay={0.1}>
                <a
                  href={`mailto:${email}`}
                  data-testid="contact-email-card"
                  className="group flex items-center gap-5 rounded-3xl border border-line bg-white p-7 transition-colors duration-300 hover:border-sage"
                >
                  <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-sage-light">
                    <Mail className="h-6 w-6 text-sage-dark" aria-hidden="true" />
                  </span>
                  <span>
                    <span className="block text-xs font-bold uppercase tracking-[0.2em] text-sage-dark">
                      Email us
                    </span>
                    <span className="mt-1 block break-all font-display text-xl font-bold text-charcoal">
                      {email}
                    </span>
                  </span>
                </a>
              </Reveal>

              <Reveal delay={0.15}>
                <div className="rounded-3xl border border-line bg-white p-7" data-testid="contact-area-card">
                  <div className="flex items-center gap-4">
                    <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-sage-light">
                      <MapPin className="h-5 w-5 text-sage-dark" aria-hidden="true" />
                    </span>
                    <h2 className="font-display text-xl font-bold text-charcoal">Servicing Perth, WA</h2>
                  </div>
                  <p className="mt-4 text-sm leading-relaxed text-ink/70">
                    We supply and install across the Perth metro area, including:
                  </p>
                  <ul className="mt-4 flex flex-wrap gap-2">
                    {SUBURBS_DETAILED.map((suburb) => (
                      <li key={suburb.slug}>
                        <Link
                          to={`/areas/${suburb.slug}`}
                          data-testid={`contact-area-${suburb.slug}-link`}
                          className="inline-flex min-h-[32px] items-center rounded-full border border-line bg-ivory px-3.5 py-1.5 text-xs font-bold text-ink/70 transition-colors duration-200 hover:border-sage hover:text-charcoal"
                        >
                          {suburb.name}
                        </Link>
                      </li>
                    ))}
                    <li className="rounded-full bg-sage-light px-3.5 py-1.5 text-xs font-bold text-sage-dark">
                      + surrounding suburbs
                    </li>
                  </ul>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
